import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  CircularProgress,
  Tooltip,
  InputAdornment,
  TablePagination,
  TableSortLabel,
  Card,
  Switch,
  FormControlLabel,
  Autocomplete,
  Checkbox,
  LinearProgress
} from '@mui/material';
import {
  RefreshCw,
  Mail as MailIcon,
  User as UserIcon,
  Users as UsersIcon,
  Lock as LockIcon,
  Wifi as WifiIcon,
  Server as ServerIcon,
  Inbox as InboxIcon,
  Send as SendIcon,
  Edit as EditIcon,
  Trash2 as DeleteIcon,
  Plus as AddIcon,
  Search as SearchIcon,
  Filter as FilterAltIcon,
  X as ClearIcon,
  CheckCircle as CheckCircleIcon,
  XCircle as CancelIcon,
  BadgeCheck as ActiveStatusIcon,
  Ban as InactiveStatusIcon,
  AlertTriangle as AlertTriangleIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Mail as EmailAccountsLogoIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';

import api from '../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const formatImapSyncError = (raw) => {
  const msg = String(raw || '').trim();
  if (!msg) return '';
  const m = msg.toLowerCase();

  if (m.includes('too many simultaneous connections')) {
    return 'Troppe connessioni contemporanee al server di posta. Riprova tra qualche secondo (chiudendo eventuali sessioni aperte) e poi rilancia la sincronizzazione.';
  }
  if (m.includes('invalid credentials') || m.includes('authentication failed') || m.includes('authentification failed') || m.includes('login failed') || m.includes('auth failed')) {
    return 'Accesso IMAP non riuscito: credenziali non valide o autorizzazione mancante. Verifica username/password e le impostazioni del provider.';
  }
  if (m.includes('etimedout') || m.includes('timed out') || m.includes('timeout') || m.includes('imap_timeout')) {
    return 'Timeout di connessione al server di posta. Riprova tra qualche secondo oppure verifica rete/host/porta.';
  }
  if (m.includes('econnrefused') || m.includes('connection refused')) {
    return 'Connessione rifiutata dal server di posta. Verifica host/porta e che il server sia raggiungibile.';
  }
  if (m.includes('enotfound') || m.includes('getaddrinfo')) {
    return 'Server di posta non raggiungibile (host non trovato). Verifica il nome host configurato e la connessione di rete.';
  }

  return msg;
};

const PROVIDERS_CONFIG = {
  'aruba_pec': {
    label: 'Aruba PEC',
    type: 'pec',
    imap_host: 'imaps.pec.aruba.it',
    imap_port: 993,
    imap_encryption: 'ssl',
    smtp_host: 'smtps.pec.aruba.it',
    smtp_port: 465,
    smtp_encryption: 'ssl'
  },
  'aruba_mail': {
    label: 'Aruba Mail',
    type: 'email',
    imap_host: 'imap.aruba.it',
    imap_port: 993,
    imap_encryption: 'ssl',
    smtp_host: 'smtp.aruba.it',
    smtp_port: 465,
    smtp_encryption: 'ssl'
  },
  'gmail': {
    label: 'Gmail',
    type: 'email',
    imap_host: 'imap.gmail.com',
    imap_port: 993,
    imap_encryption: 'ssl',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 465,
    smtp_encryption: 'ssl'
  },
  'outlook': {
    label: 'Outlook/Hotmail',
    type: 'email',
    imap_host: 'outlook.office365.com',
    imap_port: 993,
    imap_encryption: 'ssl',
    smtp_host: 'smtp.office365.com',
    smtp_port: 587,
    smtp_encryption: 'tls'
  },
  'custom': {
    label: 'Configurazione Personalizzata',
    type: 'email',
    // Non sovrascriviamo i valori se si sceglie custom, o li lasciamo vuoti/default
  }
};

export default function EmailAccounts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = String(user?.role_name || user?.role || '').toLowerCase() === 'admin';
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [accountDialogTab, setAccountDialogTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testTarget, setTestTarget] = useState(null);
  const [openInboxInfo, setOpenInboxInfo] = useState(false);
  const [inboxInfoAccount, setInboxInfoAccount] = useState(null);
  const [dontShowInboxInfo, setDontShowInboxInfo] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncDialogAccount, setSyncDialogAccount] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncStatusLoading, setSyncStatusLoading] = useState(false);
  const [syncFolders, setSyncFolders] = useState([]);
  const [syncFolder, setSyncFolder] = useState('__ALL__');
  const [syncIncludeAllAttachments, setSyncIncludeAllAttachments] = useState(false);
  const [syncFoldersLoading, setSyncFoldersLoading] = useState(false);
  
  // Pagination & Sorting
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [orderBy, setOrderBy] = useState('created_at');
  const [orderDirection, setOrderDirection] = useState('desc');

  // Filters
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const activeFiltersCount = [
    filterType !== 'all' ? filterType : null,
    filterStatus !== 'all' ? filterStatus : null
  ].filter(Boolean).length;

  // Password Visibility State
  const [showImapPassword, setShowImapPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'email',
    provider: 'custom',
    is_active: true,
    imap_host: '',
    imap_port: 993,
    imap_encryption: 'ssl',
    imap_username: '',
    imap_password: '',
    imap_validate_cert: true,
    smtp_host: '',
    smtp_port: 587,
    smtp_encryption: 'tls',
    smtp_username: '',
    smtp_password: ''
  });

  const [testingConnection, setTestingConnection] = useState(false);

  const [utentiAbilitatiLoading, setUtentiAbilitatiLoading] = useState(false);
  const [utentiAbilitati, setUtentiAbilitati] = useState([]);
  const [utentiSearch, setUtentiSearch] = useState('');
  const [utentiOptionsLoading, setUtentiOptionsLoading] = useState(false);
  const [utentiOptions, setUtentiOptions] = useState([]);
  const [utenteSelezionato, setUtenteSelezionato] = useState(null);

  const formatRelativeTime = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return 'adesso';
    const sec = Math.floor(diffMs / 1000);
    if (sec < 60) return 'adesso';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}min fa`;
    const hours = Math.floor(min / 60);
    if (hours < 24) return `${hours}h fa`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} gg`;
    const months = Math.floor(days / 30);
    if (months < 12) return months === 1 ? '1 mese' : `${months} mesi`;
    const years = Math.floor(months / 12);
    return `${years} anno${years === 1 ? '' : 'i'}`;
  };

  const toBool = (value, defaultValue = true) => {
    if (value === false) return false;
    if (value === 0) return false;
    if (value === '0') return false;
    if (typeof value === 'string' && value.toLowerCase() === 'false') return false;
    if (value == null) return defaultValue;
    return Boolean(value);
  };

  const getSyncStatusConfig = (status) => {
    if (status === 'syncing' || status === 'running') {
      return { color: 'warning.main', label: 'In corso' };
    }
    if (status === 'success' || status === 'idle') {
      return { color: 'success.main', label: 'Pronto' };
    }
    if (status === 'error') {
      return { color: 'error.main', label: 'Errore' };
    }
    return { color: 'text.disabled', label: 'Mai eseguita' };
  };

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search,
        type: filterType !== 'all' ? filterType : undefined,
        is_active: filterStatus !== 'all' ? (filterStatus === 'active' ? 1 : 0) : undefined,
        sort: orderBy,
        order: orderDirection
      };

      const response = await api.get('/email-accounts', { params });
      
      if (response.data && Array.isArray(response.data.data)) {
        setAccounts(response.data.data);
        setTotal(response.data.total);
      } else {
        setAccounts([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching email accounts:', error);
      toast.error('Errore nel caricamento degli account email');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, filterType, filterStatus, orderBy, orderDirection]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && orderDirection === 'asc';
    setOrderDirection(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const loadUtentiAbilitati = async (accountId) => {
    if (!accountId) {
      setUtentiAbilitati([]);
      return;
    }
    setUtentiAbilitatiLoading(true);
    try {
      const res = await api.get(`/email-accounts/${accountId}/utenti-abilitati`);
      const utenti = Array.isArray(res.data?.utenti) ? res.data.utenti : [];
      setUtentiAbilitati(
        utenti.map((u) => ({
          id: u.user_id,
          name: u.nome || u.name || '',
          email: u.email || '',
          permesso_lettura: u.permesso_lettura ? 1 : 0,
          permesso_invio: u.permesso_invio ? 1 : 0,
          permesso_gestione: u.permesso_gestione ? 1 : 0
        }))
      );
    } catch (e) {
      console.error('Errore caricamento utenti abilitati:', e);
      toast.error('Errore caricamento utenti abilitati');
      setUtentiAbilitati([]);
    } finally {
      setUtentiAbilitatiLoading(false);
    }
  };

  useEffect(() => {
    if (!openDialog || accountDialogTab !== 3) return;
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setUtentiOptionsLoading(true);
      try {
        const res = await api.get('/users', {
          params: { page: 1, limit: 20, search: utentiSearch || '' }
        });
        const rows = Array.isArray(res.data?.users) ? res.data.users : [];
        const opts = rows.map((u) => ({ id: u.id, name: u.name, email: u.email }));
        if (!cancelled) setUtentiOptions(opts);
      } catch (e) {
        if (!cancelled) setUtentiOptions([]);
      } finally {
        if (!cancelled) setUtentiOptionsLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [openDialog, accountDialogTab, utentiSearch]);

  const handleOpenDialog = (account = null) => {
    setAccountDialogTab(0);
    setUtentiSearch('');
    setUtentiOptions([]);
    setUtenteSelezionato(null);
    if (account) {
      setCurrentAccount(account);
      setFormData({
        name: account.name,
        email: account.email,
        type: account.type,
        provider: account.provider || 'custom',
        is_active: Boolean(account.is_active),
        imap_host: account.imap_host,
        imap_port: account.imap_port,
        imap_encryption: account.imap_encryption,
        imap_username: account.imap_username,
        imap_password: '',
        imap_validate_cert: toBool(account.imap_validate_cert, true),
        smtp_host: account.smtp_host,
        smtp_port: account.smtp_port,
        smtp_encryption: account.smtp_encryption,
        smtp_username: account.smtp_username,
        smtp_password: ''
      });
      setShowImapPassword(false);
      setShowSmtpPassword(false);
      loadUtentiAbilitati(account.id);
    } else {
      setCurrentAccount(null);
      setFormData({
        name: '',
        email: '',
        type: 'email',
        provider: 'custom',
        is_active: true,
        imap_host: '',
        imap_port: 993,
        imap_encryption: 'ssl',
        imap_username: '',
        imap_password: '',
        imap_validate_cert: true,
        smtp_host: '',
        smtp_port: 587,
        smtp_encryption: 'tls',
        smtp_username: '',
        smtp_password: ''
      });
      setShowImapPassword(false);
      setShowSmtpPassword(false);
      setUtentiAbilitati([]);
      setUtenteSelezionato(null);
    }
    setOpenDialog(true);
  };

  const handleProviderChange = (e) => {
    const newProvider = e.target.value;
    const config = PROVIDERS_CONFIG[newProvider];
    
    let updates = { provider: newProvider };
    
    if (newProvider !== 'custom' && config) {
        updates = {
            ...updates,
            type: config.type,
            imap_host: config.imap_host,
            imap_port: config.imap_port,
            imap_encryption: config.imap_encryption,
            smtp_host: config.smtp_host,
            smtp_port: config.smtp_port,
            smtp_encryption: config.smtp_encryption
        };
    }
    
    setFormData({ ...formData, ...updates });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSave = async () => {
    try {
      const payload = { ...formData };
      if (currentAccount) {
        if (!payload.imap_password) delete payload.imap_password;
        if (!payload.smtp_password) delete payload.smtp_password;
      }
      let accountId = currentAccount?.id || null;
      if (currentAccount) {
        await api.put(`/email-accounts/${currentAccount.id}`, payload);
        toast.success('Account aggiornato con successo');
      } else {
        const res = await api.post('/email-accounts', payload);
        accountId = res?.data?.id || null;
        toast.success('Account creato con successo');
      }

      if (accountId) {
        try {
          await api.put(`/email-accounts/${accountId}/utenti-abilitati`, {
            utenti: (utentiAbilitati || []).map((u) => ({
              user_id: u.id,
              permesso_lettura: 1,
              permesso_invio: u.permesso_invio ? 1 : 0,
              permesso_gestione: u.permesso_gestione ? 1 : 0
            }))
          });
        } catch (e) {
          console.error('Errore salvataggio utenti abilitati:', e);
          toast.error('Account salvato, ma errore nel salvataggio utenti abilitati');
          return;
        }
      }

      handleCloseDialog();
      fetchAccounts();
    } catch (error) {
      console.error('Error saving email account:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio');
    }
  };

  const handleDelete = (account) => {
    setDeleteTarget(account);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (deleteSubmitting) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/email-accounts/${deleteTarget.id}`);
      toast.success('Account eliminato');
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting email account:', error);
      toast.error('Errore durante l\'eliminazione');
    } finally {
      setDeleteSubmitting(false);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    }
  };

  const handleTestConnection = async (id) => {
    setTestingConnection(true);
    try {
        const response = await api.post(`/email-accounts/${id}/test-connection`);
        toast.success(response.data.message || 'Connessione riuscita!');
        return true;
    } catch (error) {
        console.error('Connection test failed:', error);
        toast.error('Connessione fallita: ' + (error.response?.data?.error || error.message || 'Errore sconosciuto'));
        return false;
    } finally {
        setTestingConnection(false);
    }
  };

  const openTestConnectionDialog = (account) => {
    setTestTarget(account);
    setTestDialogOpen(true);
  };

  const closeTestConnectionDialog = () => {
    if (testingConnection) return;
    setTestDialogOpen(false);
    setTestTarget(null);
  };

  const handleConfirmTestConnection = async () => {
    if (!testTarget?.id) return;
    await handleTestConnection(testTarget.id);
    closeTestConnectionDialog();
  };

  const fetchFolders = useCallback(async (accountId) => {
    if (!accountId) return [];
    setSyncFoldersLoading(true);
    try {
      const response = await api.get(`/email-accounts/${accountId}/folders`);
      const folders = Array.isArray(response.data) ? response.data : [];
      setSyncFolders(folders);
      return folders;
    } catch (e) {
      toast.error('Errore nel recupero delle cartelle IMAP');
      setSyncFolders([]);
      return [];
    } finally {
      setSyncFoldersLoading(false);
    }
  }, []);

  const openSyncDialog = (account) => {
    setSyncDialogAccount(account);
    setSyncStatus(null);
    setSyncFolders([]);
    setSyncFolder('__ALL__');
    setSyncIncludeAllAttachments(false);
    setSyncDialogOpen(true);
    fetchFolders(account?.id).then((folders) => {
      const paths = folders.map((f) => f.path);
      if (paths.length > 0 && syncFolder !== '__ALL__') {
        const defaultFolder = paths.includes('INBOX') ? 'INBOX' : (paths[0] || 'INBOX');
        setSyncFolder(defaultFolder);
      }
    });
  };

  const closeSyncDialog = () => {
    setSyncDialogOpen(false);
    setSyncDialogAccount(null);
    setSyncStatus(null);
    setSyncFolders([]);
  };

  const fetchSyncStatus = useCallback(async (accountId, folder = 'INBOX') => {
    if (!accountId) return;
    setSyncStatusLoading(true);
    try {
      const response = await api.get(`/email-accounts/${accountId}/sync-status`, {
        params: { folder }
      });
      setSyncStatus(response.data || null);
    } catch (e) {
      toast.error('Errore nel recupero dello stato di sincronizzazione');
    } finally {
      setSyncStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!syncDialogOpen || !syncDialogAccount?.id) return;
    const accountId = syncDialogAccount.id;
    const folder = syncFolder || 'INBOX';
    fetchSyncStatus(accountId, folder);
    const interval = window.setInterval(() => {
      fetchSyncStatus(accountId, folder);
    }, 1500);
    return () => {
      window.clearInterval(interval);
    };
  }, [syncDialogOpen, syncDialogAccount, fetchSyncStatus, syncFolder]);

  const handleForceSync = async (account, folderOverride, includeAllAttachmentsOverride) => {
    try {
      const folder = folderOverride || 'INBOX';
      const includeAll = Boolean(includeAllAttachmentsOverride);
      const payload = { folder };
      if (includeAll) payload.attachment_mode = 'all';
      await api.post(`/email-accounts/${account.id}/sync`, payload);
      toast.success('Sincronizzazione avviata');
      if (syncDialogOpen && syncDialogAccount?.id === account.id) {
        fetchSyncStatus(account.id, folder);
      } else {
        openSyncDialog(account);
      }
      fetchAccounts();
    } catch (e) {
      toast.error('Errore avviando la sincronizzazione');
    }
  };

  const handleViewEmails = (account) => {
    const dismissed = window.localStorage.getItem('hide_email_inbox_info') === '1';
    if (dismissed) {
      navigate(`/email-accounts/${account.id}/inbox`);
      return;
    }
    setInboxInfoAccount(account);
    setDontShowInboxInfo(false);
    setOpenInboxInfo(true);
  };

  const handleCloseInboxInfo = () => {
    setOpenInboxInfo(false);
    setInboxInfoAccount(null);
  };

  const handleContinueToInbox = () => {
    if (dontShowInboxInfo) {
      window.localStorage.setItem('hide_email_inbox_info', '1');
    }
    if (inboxInfoAccount?.id) {
      navigate(`/email-accounts/${inboxInfoAccount.id}/inbox`);
    }
    handleCloseInboxInfo();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <EmailAccountsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Account Email / PEC</Typography>
            <Typography variant="body2" color="text.secondary">
              Configurazione caselle email e PEC per invio/ricezione
            </Typography>
          </Box>
        </Box>
        {isAdmin ? (
          <Button
            variant="default"
            onClick={() => handleOpenDialog()}
            className="gap-2 bg-slate-900 text-white hover:bg-slate-800 shadow-sm dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
          >
            <AddIcon size={16} />
            Nuovo Account
          </Button>
        ) : null}
      </Box>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth disableEscapeKeyDown={deleteSubmitting}>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.20)' : 'rgba(239, 68, 68, 0.12)'
            })}
          >
            <DeleteIcon size={18} color="#ef4444" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 800 }}>
              Elimina account
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {deleteTarget?.name ? `${deleteTarget.name} • ${deleteTarget.email}` : (deleteTarget?.email || '')}
            </Typography>
          </Box>
          {deleteSubmitting && (
            <Chip
              label="Eliminazione…"
              color="error"
              variant="outlined"
              size="small"
              icon={<CircularProgress size={14} />}
              sx={{ ml: 'auto' }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Stai per eliminare definitivamente questo account dal sistema.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: 'divider',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.06)'
                  : 'rgba(0, 0, 0, 0.04)'
            }}
          >
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <AlertTriangleIcon size={18} color="#f59e0b" style={{ marginTop: 2 }} />
                <Typography variant="body2">
                  Verranno eliminate tutte le email e gli allegati associati a questo account.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <CancelIcon size={18} color="#ef4444" style={{ marginTop: 2 }} />
                <Typography variant="body2">Questa azione è irreversibile.</Typography>
              </Box>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outline"
            onClick={handleCloseDeleteDialog}
            disabled={deleteSubmitting}
            className="border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 dark:border-white/25 dark:bg-transparent dark:text-white/85 dark:hover:bg-white/10 dark:hover:text-white"
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={deleteSubmitting}
            className="bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
          >
            {deleteSubmitting ? 'Conferma…' : 'Conferma'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={testDialogOpen}
        onClose={closeTestConnectionDialog}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={testingConnection}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.20)' : 'rgba(59, 130, 246, 0.12)'
            })}
          >
            <WifiIcon size={18} color="#3b82f6" />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 800 }}>
              Test connessione
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {testTarget?.name ? `${testTarget.name} • ${testTarget.email}` : (testTarget?.email || '')}
            </Typography>
          </Box>
          {testingConnection && (
            <Chip
              label="Test in corso…"
              color="info"
              variant="outlined"
              size="small"
              icon={<CircularProgress size={14} />}
              sx={{ ml: 'auto' }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Verifica che IMAP e SMTP siano configurati correttamente e che le credenziali salvate funzionino.
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: 'divider',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.06)'
                  : 'rgba(0, 0, 0, 0.04)'
            }}
          >
            <Box sx={{ display: 'grid', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <ServerIcon size={18} color="#3b82f6" style={{ marginTop: 2 }} />
                <Typography variant="body2">Controlla la raggiungibilità dei server IMAP/SMTP configurati.</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <MailIcon size={18} color="#22c55e" style={{ marginTop: 2 }} />
                <Typography variant="body2">Non scarica email e non modifica la casella.</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <SendIcon size={18} color="#f59e0b" style={{ marginTop: 2 }} />
                <Typography variant="body2">Non invia messaggi: esegue solo un test di connessione.</Typography>
              </Box>
            </Box>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outline"
            onClick={closeTestConnectionDialog}
            disabled={testingConnection}
            className="border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 dark:border-white/25 dark:bg-transparent dark:text-white/85 dark:hover:bg-white/10 dark:hover:text-white"
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirmTestConnection}
            disabled={testingConnection}
            className="bg-slate-900 text-white hover:bg-slate-800 shadow-sm dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
          >
            {testingConnection ? 'Avvio…' : 'Continua'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toolbar */}
      <Card sx={{ mb: 3 }}>
        <Grid container spacing={2} sx={{ p: 2, alignItems: 'center' }}>
          <Grid item xs={12} md={showFilters ? 8 : 12}>
            <TextField
              size="small"
              fullWidth
              placeholder="Cerca account..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={18} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {search && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearch('');
                            setPage(0);
                          }}
                        >
                          <ClearIcon size={18} />
                        </IconButton>
                      )}
                      <IconButton
                        onClick={() => setShowFilters((prev) => !prev)}
                        color={showFilters || activeFiltersCount > 0 ? 'primary' : 'default'}
                      >
                        <Badge badgeContent={activeFiltersCount} color="primary">
                          <FilterAltIcon size={18} />
                        </Badge>
                      </IconButton>
                    </Box>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {showFilters && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Account</InputLabel>
                <Select
                  value={filterType}
                  label="Tipo Account"
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="email">Email Standard</MenuItem>
                  <MenuItem value="pec">PEC</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          {showFilters && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Stato</InputLabel>
                <Select
                  value={filterStatus}
                  label="Stato"
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="all">Tutti</MenuItem>
                  <MenuItem value="active">Attivi</MenuItem>
                  <MenuItem value="inactive">Inattivi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Table Section */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'background.neutral' }}>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'name'}
                    direction={orderBy === 'name' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('name')}
                  >
                    Account
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>Provider</TableCell>
                <TableCell align="center">Accesso</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'is_active'}
                    direction={orderBy === 'is_active' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('is_active')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'last_sync_at'}
                    direction={orderBy === 'last_sync_at' ? orderDirection : 'asc'}
                    onClick={() => handleRequestSort('last_sync_at')}
                  >
                    Ultima Sync
                  </TableSortLabel>
                </TableCell>
                <TableCell>Sync Status</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" className="py-8">
                    <RefreshCw className="animate-spin h-6 w-6 text-gray-400 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">Nessun account email trovato</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow key={account.id} hover>
                    <TableCell sx={{ minWidth: 260 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                          {account.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                          <Box
                            sx={(theme) => ({
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flex: '0 0 auto',
                              color: account.type === 'pec' ? theme.palette.secondary.main : theme.palette.primary.main
                            })}
                          >
                            <MailIcon size={14} />
                          </Box>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {account.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={account.type.toUpperCase()} 
                        color={account.type === 'pec' ? 'secondary' : 'primary'} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                        {PROVIDERS_CONFIG[account.provider]?.label || 'Custom'}
                    </TableCell>
                    <TableCell align="center">
                      {Number(account.has_acl) === 1 ? (
                        <Tooltip title={`Limitato: accesso solo agli utenti abilitati (${Number(account.utenti_abilitati_count || 0)})`}>
                          <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Box
                              sx={(theme) => ({
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color:
                                  Number(account.utenti_abilitati_count || 0) === 0
                                    ? 'error.main'
                                    : 'warning.main',
                                bgcolor:
                                  theme.palette.mode === 'dark'
                                    ? Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.20)'
                                      : 'rgba(245, 158, 11, 0.18)'
                                    : Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.12)'
                                      : 'rgba(245, 158, 11, 0.12)',
                                border: '1px solid',
                                borderColor:
                                  theme.palette.mode === 'dark'
                                    ? Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.45)'
                                      : 'rgba(245, 158, 11, 0.45)'
                                    : Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.28)'
                                      : 'rgba(245, 158, 11, 0.28)'
                              })}
                            >
                              <LockIcon size={18} />
                            </Box>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.35
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color:
                                    Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'error.main'
                                      : 'warning.main'
                                }}
                              >
                                <UsersIcon size={12} />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 800, lineHeight: 1 }}
                                color={
                                  Number(account.utenti_abilitati_count || 0) === 0
                                    ? 'error.main'
                                    : 'text.secondary'
                                }
                              >
                                {Number(account.utenti_abilitati_count || 0)}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title={`Non assegnato: nessun utente abilitato (${Number(account.utenti_abilitati_count || 0)}). Visibile solo agli admin; per gli altri utenti non compare in lista.`}>
                          <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                            <Box
                              sx={(theme) => ({
                                width: 34,
                                height: 34,
                                borderRadius: '50%',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color:
                                  Number(account.utenti_abilitati_count || 0) === 0
                                    ? 'error.main'
                                    : 'text.secondary',
                                bgcolor:
                                  theme.palette.mode === 'dark'
                                    ? Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.20)'
                                      : 'rgba(148, 163, 184, 0.18)'
                                    : Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.12)'
                                      : 'rgba(148, 163, 184, 0.12)',
                                border: '1px solid',
                                borderColor:
                                  theme.palette.mode === 'dark'
                                    ? Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.45)'
                                      : 'rgba(148, 163, 184, 0.45)'
                                    : Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'rgba(239, 68, 68, 0.28)'
                                      : 'rgba(148, 163, 184, 0.28)'
                              })}
                            >
                              <UserIcon size={18} />
                            </Box>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.35
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color:
                                    Number(account.utenti_abilitati_count || 0) === 0
                                      ? 'error.main'
                                      : 'text.secondary'
                                }}
                              >
                                <UsersIcon size={12} />
                              </Box>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 800, lineHeight: 1 }}
                                color={
                                  Number(account.utenti_abilitati_count || 0) === 0
                                    ? 'error.main'
                                    : 'text.secondary'
                                }
                              >
                                {Number(account.utenti_abilitati_count || 0)}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={account.is_active ? 'Attivo' : 'Inattivo'}>
                        <Box
                          sx={(theme) => ({
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: account.is_active ? 'success.main' : 'error.main',
                            bgcolor: account.is_active
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(34, 197, 94, 0.20)'
                                : 'rgba(34, 197, 94, 0.12)'
                              : theme.palette.mode === 'dark'
                                ? 'rgba(239, 68, 68, 0.20)'
                                : 'rgba(239, 68, 68, 0.12)',
                            border: '1px solid',
                            borderColor: account.is_active
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(34, 197, 94, 0.45)'
                                : 'rgba(34, 197, 94, 0.25)'
                              : theme.palette.mode === 'dark'
                                ? 'rgba(239, 68, 68, 0.45)'
                                : 'rgba(239, 68, 68, 0.25)'
                          })}
                        >
                          {account.is_active ? (
                            <ActiveStatusIcon size={18} />
                          ) : (
                            <InactiveStatusIcon size={18} />
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                        <Box>
                          {account.last_sync_at ? (
                            <>
                              <Typography
                                variant="body2"
                                sx={(theme) => {
                                  const date = new Date(account.last_sync_at);
                                  const now = new Date();
                                  const diffHours = (now - date) / (1000 * 60 * 60);
                                  if (diffHours < 24) return { color: 'success.main', fontWeight: 800 };
                                  if (diffHours < 24 * 7) return { color: 'warning.main', fontWeight: 700 };
                                  return { color: 'error.main', fontWeight: 700 };
                                }}
                              >
                                {new Date(account.last_sync_at).toLocaleString('it-IT', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatRelativeTime(account.last_sync_at)}
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                          <Tooltip title="Totale messaggi indicizzati in archivio (Email/PEC)">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
                              <Box
                                sx={(theme) => ({
                                  width: 22,
                                  height: 22,
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flex: '0 0 auto',
                                  color:
                                    account.type === 'pec'
                                      ? theme.palette.secondary.main
                                      : theme.palette.primary.main,
                                  bgcolor:
                                    theme.palette.mode === 'dark'
                                      ? account.type === 'pec'
                                        ? 'rgba(168, 85, 247, 0.18)'
                                        : 'rgba(37, 99, 235, 0.18)'
                                      : account.type === 'pec'
                                        ? 'rgba(168, 85, 247, 0.12)'
                                        : 'rgba(37, 99, 235, 0.12)',
                                  border: '1px solid',
                                  borderColor:
                                    theme.palette.mode === 'dark'
                                      ? account.type === 'pec'
                                        ? 'rgba(168, 85, 247, 0.45)'
                                        : 'rgba(37, 99, 235, 0.45)'
                                      : account.type === 'pec'
                                        ? 'rgba(168, 85, 247, 0.28)'
                                        : 'rgba(37, 99, 235, 0.28)'
                                })}
                              >
                                <InboxIcon size={14} />
                              </Box>
                              <Typography variant="caption" sx={{ fontWeight: 800 }} color="text.secondary" noWrap>
                                {Number(account.emails_total || 0).toLocaleString('it-IT')}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const cfg = getSyncStatusConfig(account.sync_status);
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: cfg.color
                              }}
                            />
                            <Tooltip
                              title={account.sync_status === 'error' ? (formatImapSyncError(account.sync_error) || 'Errore sincronizzazione') : ''}
                              disableHoverListener={account.sync_status !== 'error'}
                            >
                              <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                {cfg.label}
                              </Typography>
                            </Tooltip>
                            <Tooltip title="Dettagli sincronizzazione">
                              <IconButton size="small" onClick={() => openSyncDialog(account)}>
                                <SearchIcon size={16} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell align="right">
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Tooltip title="Vedi Email">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewEmails(account)}
                          >
                            <Box
                              sx={(theme) => ({
                                position: 'relative',
                                width: 18,
                                height: 18,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.palette.text.primary
                              })}
                            >
                              <UserIcon size={18} />
                              <Box
                                sx={(theme) => ({
                                  position: 'absolute',
                                  right: -4,
                                  bottom: -4,
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: 'primary.main',
                                  border: '2px solid',
                                  borderColor: theme.palette.background.paper
                                })}
                              >
                                <MailIcon size={10} color="#ffffff" />
                              </Box>
                            </Box>
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Test Connessione">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => openTestConnectionDialog(account)}
                            disabled={testingConnection}
                          >
                            <WifiIcon size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sincronizzazione">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => openSyncDialog(account)}
                          >
                            <RefreshCw size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isAdmin ? 'Modifica' : 'Solo admin'}>
                          <span>
                            <IconButton size="small" onClick={() => handleOpenDialog(account)} disabled={!isAdmin}>
                              <EditIcon size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isAdmin ? 'Elimina' : 'Solo admin'}>
                          <span>
                            <IconButton size="small" color="error" onClick={() => handleDelete(account)} disabled={!isAdmin}>
                              <DeleteIcon size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </Paper>

      {/* Dialog Form */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={(theme) => ({
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#eef2ff'
              })}
            >
              <EmailAccountsLogoIcon size={20} color="#4f46e5" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {currentAccount ? 'Modifica account' : 'Nuovo account'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {formData.email || 'Configurazione IMAP/SMTP'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <Tabs
          value={accountDialogTab}
          onChange={(e, v) => setAccountDialogTab(v)}
          variant="fullWidth"
          sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab label="Generali" />
          <Tab label="IMAP" />
          <Tab label="SMTP" />
          <Tab label="Utenti abilitati" />
        </Tabs>
        <DialogContent sx={{ pt: 2.5 }}>
          {accountDialogTab === 0 && (
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MailIcon size={16} className="text-slate-500" />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Informazioni generali
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Nome account"
                    fullWidth
                    size="small"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Indirizzo email"
                    fullWidth
                    size="small"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Provider</InputLabel>
                    <Select value={formData.provider || 'custom'} label="Provider" onChange={handleProviderChange}>
                      {Object.entries(PROVIDERS_CONFIG).map(([key, config]) => (
                        <MenuItem key={key} value={key}>
                          {config.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo</InputLabel>
                    <Select value={formData.type} label="Tipo" onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="pec">PEC</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Stato</InputLabel>
                    <Select
                      value={formData.is_active ? 'active' : 'inactive'}
                      label="Stato"
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'active' })}
                    >
                      <MenuItem value="active">Attivo</MenuItem>
                      <MenuItem value="inactive">Inattivo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          )}

          {accountDialogTab === 1 && (
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ServerIcon size={16} className="text-blue-600" />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Ricezione (IMAP)
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Server IMAP"
                    fullWidth
                    size="small"
                    value={formData.imap_host}
                    onChange={(e) => setFormData({ ...formData, imap_host: e.target.value })}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="Porta"
                    type="number"
                    fullWidth
                    size="small"
                    value={formData.imap_port}
                    onChange={(e) => setFormData({ ...formData, imap_port: e.target.value })}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small" sx={{ bgcolor: 'background.paper' }}>
                    <InputLabel>Crittografia</InputLabel>
                    <Select value={formData.imap_encryption} label="Crittografia" onChange={(e) => setFormData({ ...formData, imap_encryption: e.target.value })}>
                      <MenuItem value="ssl">SSL</MenuItem>
                      <MenuItem value="tls">TLS</MenuItem>
                      <MenuItem value="none">Nessuna</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Username IMAP"
                    fullWidth
                    size="small"
                    value={formData.imap_username}
                    onChange={(e) => setFormData({ ...formData, imap_username: e.target.value })}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Password IMAP"
                    type={showImapPassword ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    value={formData.imap_password}
                    onChange={(e) => setFormData({ ...formData, imap_password: e.target.value })}
                    placeholder={currentAccount ? 'Lascia vuoto per non modificare' : undefined}
                    helperText={currentAccount ? 'Lascia vuoto per non modificare la password salvata' : undefined}
                    sx={{ bgcolor: 'background.paper' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowImapPassword(!showImapPassword)} edge="end" size="small">
                            {showImapPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={Boolean(formData.imap_validate_cert)} onChange={(e) => setFormData({ ...formData, imap_validate_cert: e.target.checked })} />}
                    label="Valida certificato TLS IMAP (consigliato)"
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          {accountDialogTab === 2 && (
            <Paper
              elevation={0}
              sx={(theme) => ({
                p: 2.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper'
              })}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SendIcon size={16} className="text-purple-600" />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Invio (SMTP)
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Server SMTP"
                    fullWidth
                    size="small"
                    value={formData.smtp_host}
                    onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField
                    label="Porta"
                    type="number"
                    fullWidth
                    size="small"
                    value={formData.smtp_port}
                    onChange={(e) => setFormData({ ...formData, smtp_port: e.target.value })}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <FormControl fullWidth size="small" sx={{ bgcolor: 'background.paper' }}>
                    <InputLabel>Crittografia</InputLabel>
                    <Select value={formData.smtp_encryption} label="Crittografia" onChange={(e) => setFormData({ ...formData, smtp_encryption: e.target.value })}>
                      <MenuItem value="ssl">SSL</MenuItem>
                      <MenuItem value="tls">TLS</MenuItem>
                      <MenuItem value="none">Nessuna</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Username SMTP"
                    fullWidth
                    size="small"
                    value={formData.smtp_username}
                    onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                    sx={{ bgcolor: 'background.paper' }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Password SMTP"
                    type={showSmtpPassword ? 'text' : 'password'}
                    fullWidth
                    size="small"
                    value={formData.smtp_password}
                    onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                    placeholder={currentAccount ? 'Lascia vuoto per non modificare' : undefined}
                    helperText={currentAccount ? 'Lascia vuoto per non modificare la password salvata' : undefined}
                    sx={{ bgcolor: 'background.paper' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowSmtpPassword(!showSmtpPassword)} edge="end" size="small">
                            {showSmtpPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          {accountDialogTab === 3 && (
            <Paper elevation={0} sx={{ p: 2.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <UserIcon size={16} className="text-emerald-600" />
                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                  Utenti abilitati
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Se non selezioni nessun utente, l’account resta visibile a tutti in sola lettura. Abilita “Invio” e/o “Gestione” per i singoli utenti.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Ricerca utenti
                    </Typography>
                    <Autocomplete
                      autoHighlight
                      value={null}
                      options={utentiOptions}
                      loading={utentiOptionsLoading}
                      inputValue={utentiSearch}
                      onInputChange={(e, v) => setUtentiSearch(v)}
                      onChange={(e, v) => {
                        if (!v?.id) return;
                        setUtentiAbilitati((prev) => {
                          const list = Array.isArray(prev) ? prev : [];
                          const exists = list.some((u) => Number(u.id) === Number(v.id));
                          if (exists) return list;
                          return [
                            ...list,
                            {
                              id: v.id,
                              name: v.name || '',
                              email: v.email || '',
                              permesso_lettura: 1,
                              permesso_invio: 0,
                              permesso_gestione: 0
                            }
                          ];
                        });
                        setUtentiSearch('');
                        setUtenteSelezionato(null);
                      }}
                      filterOptions={(options, state) => {
                        const q = String(state.inputValue || '').trim().toLowerCase();
                        if (!q) return options;
                        return (options || []).filter((o) => `${o?.name || ''} ${o?.email || ''}`.toLowerCase().includes(q));
                      }}
                      isOptionEqualToValue={(opt, val) => Number(opt.id) === Number(val?.id)}
                      getOptionLabel={(opt) => {
                        const name = opt?.name || '';
                        const email = opt?.email || '';
                        if (name && email) return `${name} (${email})`;
                        return name || email || '';
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {option.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.email}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Seleziona utenti"
                          size="small"
                          placeholder="Cerca per nome o email…"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {utentiOptionsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Premi Invio per aggiungere. Selezionati: {(utentiAbilitati || []).length}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                      Utenti selezionati
                    </Typography>
                    {utentiAbilitatiLoading && <LinearProgress sx={{ mb: 1.5 }} />}
                    <TableContainer sx={{ maxHeight: 320 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>Utente</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, width: 90 }}>Invio</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, width: 110 }}>Gestione</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, width: 46 }} />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(utentiAbilitati || []).length === 0 && !utentiAbilitatiLoading ? (
                            <TableRow>
                              <TableCell colSpan={3}>
                                <Typography variant="body2" color="text.secondary">
                                  Nessun utente selezionato.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            (utentiAbilitati || []).map((u) => (
                              <TableRow key={u.id} hover>
                                <TableCell>
                                  <Tooltip title={u.email || ''} disableHoverListener={!u.email}>
                                    <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                                      {u.name || `Utente #${u.id}`}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={Boolean(u.permesso_invio)}
                                    onChange={(e) => {
                                      const checked = e.target.checked ? 1 : 0;
                                      setUtentiAbilitati((prev) =>
                                        (prev || []).map((row) => (Number(row.id) === Number(u.id) ? { ...row, permesso_invio: checked } : row))
                                      );
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={Boolean(u.permesso_gestione)}
                                    onChange={(e) => {
                                      const checked = e.target.checked ? 1 : 0;
                                      setUtentiAbilitati((prev) =>
                                        (prev || []).map((row) =>
                                          Number(row.id) === Number(u.id) ? { ...row, permesso_gestione: checked } : row
                                        )
                                      );
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Rimuovi utente">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setUtentiAbilitati((prev) => (Array.isArray(prev) ? prev : []).filter((row) => Number(row.id) !== Number(u.id)));
                                      }}
                                    >
                                      <ClearIcon size={16} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="outline"
            onClick={handleCloseDialog}
            className="border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 dark:border-white/25 dark:bg-transparent dark:text-white/85 dark:hover:bg-white/10 dark:hover:text-white"
          >
            Annulla
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
          >
            {currentAccount ? 'Salva' : 'Crea account'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openInboxInfo} onClose={handleCloseInboxInfo} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MailIcon size={18} color="#4f46e5" />
          </Box>
          Accesso casella e sincronizzazione
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aprendo la casella <strong>{inboxInfoAccount?.email || ''}</strong> il sistema mostra subito le email già indicizzate e avvia una sincronizzazione incrementale in background.
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <RefreshCw size={16} color="#16a34a" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Sync incrementale e sicura</Typography>
                <Typography variant="body2" color="text.secondary">
                  Vengono scaricate solo le nuove email rispetto all’ultimo UID processato. Se il server cambia UIDVALIDITY, la cartella viene reinizializzata per evitare incongruenze.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <FilterAltIcon size={16} color="#2563eb" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Caselle molto grandi</Typography>
                <Typography variant="body2" color="text.secondary">
                  La sincronizzazione lavora a batch e ha un limite per singola esecuzione: questo mantiene reattiva l’interfaccia anche con migliaia di email.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <MailIcon size={16} color="#f97316" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>PEC e ricevute</Typography>
                <Typography variant="body2" color="text.secondary">
                  Le ricevute PEC vengono classificate tramite header (non solo subject) e collegate al messaggio originale quando possibile.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: '#faf5ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                <CheckCircleIcon size={16} color="#7c3aed" />
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Allegati su Wasabi</Typography>
                <Typography variant="body2" color="text.secondary">
                  Gli allegati vengono archiviati su Wasabi per non appesantire il database. Al download, se necessario, vengono recuperati e indicizzati.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={dontShowInboxInfo}
                  onChange={(e) => setDontShowInboxInfo(e.target.checked)}
                />
              }
              label="Non mostrare più questo messaggio"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outline"
            onClick={handleCloseInboxInfo}
            className="border-2 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 dark:border-white/25 dark:bg-transparent dark:text-white/85 dark:hover:bg-white/10 dark:hover:text-white"
          >
            Annulla
          </Button>
          <Button
            variant="default"
            onClick={handleContinueToInbox}
            className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700"
          >
            Continua
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={syncDialogOpen} onClose={closeSyncDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <RefreshCw size={18} color="#2563eb" />
          </Box>
          Stato sincronizzazione
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Account: <strong>{syncDialogAccount?.email || ''}</strong>
          </Typography>

          <Box sx={{ display: 'grid', gap: 1.5, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Cartella</InputLabel>
              <Select
                value={syncFolder}
                label="Cartella"
                onChange={(e) => setSyncFolder(e.target.value)}
                disabled={syncFoldersLoading}
              >
                <MenuItem value="__ALL__">Tutte le cartelle</MenuItem>
                {(syncFolders || []).map((f) => (
                  <MenuItem key={f.path} value={f.path}>
                    {f.path} ({f.unread || 0}/{f.total || 0})
                  </MenuItem>
                ))}
                {(!syncFolders || syncFolders.length === 0) && (
                  <MenuItem value="INBOX">INBOX</MenuItem>
                )}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={syncIncludeAllAttachments}
                  onChange={(e) => setSyncIncludeAllAttachments(e.target.checked)}
                />
              }
              label="Indicizza allegati anche per email non-PEC (più lento)"
            />
          </Box>

          {syncStatusLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
            </Box>
          )}

          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="body2">
              Stato: <strong>{syncStatus?.sync_status || '-'}</strong>
            </Typography>
            <Typography variant="body2">
              Ultima sync: <strong>{syncStatus?.last_sync_at ? new Date(syncStatus.last_sync_at).toLocaleString('it-IT') : '-'}</strong>
            </Typography>
            {syncStatus?.sync_error && (
              <Typography variant="body2" sx={{ color: 'error.main' }}>
                Errore: <strong>{formatImapSyncError(syncStatus.sync_error)}</strong>
              </Typography>
            )}
          </Box>

          {syncStatus?.progress && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {(() => {
                  const processed = Number(syncStatus.progress.processed || 0);
                  const total = Number(syncStatus.progress.total || 0);
                  const pct = total > 0 ? Math.min(100, (processed / total) * 100) : 0;
                  return `Avanzamento: ${processed} / ${total} • ${Math.round(pct)}%`;
                })()}
              </Typography>
              {(() => {
                const p = syncStatus.progress || null;
                const tailTotal = Number(p?.tail?.total || 0);
                const tailProcessed = Number(p?.tail?.processed || 0);
                const newTotal = Number(p?.new?.total || 0);
                const newProcessed = Number(p?.new?.processed || 0);
                const mode = String(p?.mode || '').toLowerCase();

                if (newTotal <= 0 && tailTotal <= 0) return null;

                const newLabel = mode === 'since' || mode === 'resync' ? 'Email indicizzate' : 'Nuove email';
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {newTotal > 0 ? `${newLabel}: ${newProcessed} / ${newTotal}` : null}
                    {newTotal > 0 && tailTotal > 0 ? ' • ' : null}
                    {tailTotal > 0 ? `Controllo: ${tailProcessed} / ${tailTotal}` : null}
                  </Typography>
                );
              })()}
              {syncStatus.progress.currentFolder && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Cartella in lavorazione: <strong>{syncStatus.progress.currentFolder}</strong>
                </Typography>
              )}
              <LinearProgress
                variant="determinate"
                value={
                  syncStatus.progress.total
                    ? Math.min(100, (Number(syncStatus.progress.processed || 0) / Number(syncStatus.progress.total || 1)) * 100)
                    : 0
                }
                sx={(theme) => {
                  const p = syncStatus.progress || null;
                  const tailTotal = Number(p?.tail?.total || 0);
                  const newTotal = Number(p?.new?.total || 0);
                  const mode = String(p?.mode || '').toLowerCase();
                  const barColor =
                    newTotal > 0
                      ? theme.palette.success.main
                      : tailTotal > 0
                        ? theme.palette.info.main
                        : (mode === 'since' || mode === 'resync')
                          ? theme.palette.primary.main
                          : theme.palette.info.main;
                  return {
                    height: 10,
                    borderRadius: 999,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.10)'
                        : 'rgba(0,0,0,0.08)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      backgroundColor: barColor
                    }
                  };
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {syncStatus.progress.startedAt ? `Avviata: ${new Date(syncStatus.progress.startedAt).toLocaleString('it-IT')}` : ''}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => fetchSyncStatus(syncDialogAccount?.id, syncFolder)} disabled={!syncDialogAccount?.id}>
            Aggiorna
          </Button>
          <Button
            onClick={() => handleForceSync(syncDialogAccount, syncFolder, syncIncludeAllAttachments)}
            disabled={!syncDialogAccount?.id}
            variant="outlined"
          >
            Avvia sync
          </Button>
          <Button onClick={closeSyncDialog} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
